

import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaFilePdf, FaWhatsapp } from 'react-icons/fa';
import { IoArrowUndoSharp } from 'react-icons/io5';
import { NavLink, useLocation, useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import AddInventory from './AddInventory';
import api from '../../services/api';
import LoadingSpinner from '../../Components/CommonComponent/loadingSpiner/LoadingSpinner';

export default function ViewInventoryModal() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const { branchId, endDate, startDate } = location.state || {};
    const [category, setCategory] = useState(location.state?.category || {});

    const closingStock =
        Number(category.opening_stock) + Number(category.in_side) - Number(category.out_side);

    // ---- Keyboard / focus state (same as LedgerDetails) ----
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const containerRef = useRef(null);
    const rowRefs = useRef([]);

    // ---- Edit modal state ----
    const [editItem, setEditItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const items = category.items || [];

    // ---- Auto focus on mount ----
    useEffect(() => {
        containerRef.current?.focus();
    }, []);

    // ---- Reset focus when data changes ----
    useEffect(() => {
        setFocusedIndex(-1);
        setSelectedRows(new Set());
    }, [category]);

    // ---- Keyboard handler ----
    const handleKeyDown = useCallback((e) => {
        if (!items.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev === -1 ? 0 : Math.min(prev + 1, items.length - 1)));
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((prev) => (prev === -1 ? items.length - 1 : Math.max(prev - 1, 0)));
        }
        if (e.key === ' ') {
            e.preventDefault();
            if (focusedIndex >= 0) {
                const id = items[focusedIndex].id;
                setSelectedRows((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(id)) newSet.delete(id);
                    else newSet.add(id);
                    return newSet;
                });
            }
        }
    }, [items, focusedIndex]);

    // ---- Global key listeners ----
    useEffect(() => {
        const handleGlobalKey = (e) => {
            if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
                if (document.activeElement !== containerRef.current) {
                    containerRef.current?.focus();
                    handleKeyDown(e);
                }
            }
            if (e.key === 'Escape') {
                navigate('/inventory-books/inventory-items-table', { state: { category } });
            }
        };
        window.addEventListener('keydown', handleGlobalKey);
        return () => window.removeEventListener('keydown', handleGlobalKey);
    }, [handleKeyDown, navigate, category]);

    // ---- Smooth scroll to focused row ----
    useLayoutEffect(() => {
        if (focusedIndex < 0) return;
        const row = rowRefs.current[focusedIndex];
        const container = containerRef.current;
        if (!row || !container) return;

        const containerHeight = container.clientHeight;
        const scrollTop = container.scrollTop;
        const rowTop = row.offsetTop;
        const rowHeight = row.offsetHeight;
        const rowCenter = rowTop + rowHeight / 2;
        const containerCenter = scrollTop + containerHeight / 2;
        const OFFSET = 60;

        if (rowCenter > containerCenter + OFFSET) {
            container.scrollTo({ top: rowTop - containerHeight / 2 + rowHeight, behavior: 'smooth' });
        } else if (rowCenter < containerCenter - OFFSET) {
            container.scrollTo({ top: rowTop - containerHeight / 2, behavior: 'smooth' });
        }
    }, [focusedIndex]);

    // ---- Save / update handler (same as original, logic unchanged) ----
    const handleAddInventory = async (form, id = null) => {
        try {
            setLoading(true);
            if (id) {
                const res = await api.post(`inventory/update/${id}`, { ...form, branch_id: branchId });
                if (res.data.success) {
                    toast.success('Updated successfully');
                    setIsModalOpen(false);
                    setEditItem(null);

                    // Refresh category data
                    const freshRes = await api.get(
                        `inventory/get-all-item?branchId=${branchId}&startDate=${startDate || ''}&endDate=${endDate || ''}`
                    );
                    if (freshRes.data.success) {
                        const allData = Array.isArray(freshRes.data.data)
                            ? freshRes.data.data
                            : freshRes.data.data?.allData || [];

                        const updatedItem = allData.find(
                            (item) => item.ledger_id === (category.ledger_id || Number(form.ledger_id))
                        );
                        if (updatedItem) setCategory(updatedItem);
                    }
                }
            } else {
                const res = await api.post(`inventory/add-new`, { ...form, branch_id: branchId });
                if (res.data.success) {
                    toast.success('Added successfully');
                    setIsModalOpen(false);
                }
            }
        } catch (error) {
            toast.error('Error in create & update item');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateItem = (item) => {
        setEditItem(item);
        setIsModalOpen(true);
    };

    // ---- Totals for footer (mirrors LedgerDetails totalReceived / totalPaid pattern) ----
    const totalIn = useMemo(
        () => items.reduce((s, r) => s + Number(r.in_side || 0), 0),
        [items]
    );
    const totalOut = useMemo(
        () => items.reduce((s, r) => s + Number(r.out_side || 0), 0),
        [items]
    );

    const hasItems = items.length > 0;

    return (
        <>
            <div className="container-fluid p-0">
                <div className="pt-2">

                    {/* ── Header row (Back btn + action buttons) ─────────────────── */}
                    <div className="flex justify-between gap-3">
                        <div className="mt-[2px]">
                            <NavLink
                                to="/inventory-books/inventory-items-table"
                                state={{ category }}
                                className="inline-block p-1 px-2 text-theme-backBtn border-1 border-theme-backBtn rounded cursor-pointer hover:bg-theme-backBtn hover:text-theme-color"
                                title="Back to items"
                            >
                                <IoArrowUndoSharp />
                            </NavLink>
                        </div>

                        {/* Placeholder action buttons – same layout as LedgerDetails PDF/WhatsApp row */}
                        {/* <div className="flex justify-end gap-2 mb-2">
                            <button
                                className="flex items-center gap-1 px-3 py-[2px] text-white bg-theme-headerBg rounded-1 opacity-50 cursor-not-allowed"
                                title="PDF (coming soon)"
                                disabled
                            >
                                <FaFilePdf /> PDF
                            </button>
                            <button
                                className="flex items-center gap-1 px-3 py-[2px] text-white bg-theme-headerBg rounded-1 opacity-50 cursor-not-allowed"
                                title="WhatsApp (coming soon)"
                                disabled
                            >
                                <FaWhatsapp /> WhatsApp
                            </button>
                        </div> */}
                    </div>

                    {/* ── Main content card (mirrors LedgerDetails inner card) ────── */}
                    <div className="p-xl-6 bg-theme-sidebarTabBg text-sm shadow-sm mx-auto h-[80vh] border">

                        {/* Account info header */}
                        <div className="bg-white text-theme-headerBg">
                            <div className="mb-2 pt-2 font-bold text-center">
                                {t('accountDetails') || 'Inventory Details'}
                            </div>

                            <div className="grid grid-cols-3 px-4 mb-1 xl:gap-1 md:gap-2 text-sm">
                                <div>
                                    <strong className="font-medium text-sm">{t('accountName') || 'Item'}:</strong>{' '}
                                    <span className="p-1 px-2 capitalize text-white font-medium bg-theme-headerBg rounded-1">
                                        {category.ledger_name}{' '}
                                        {category.ledgerNameInHindi && `(${category.ledgerNameInHindi})`}
                                    </span>
                                </div>
                                <div>
                                    <strong className="font-medium text-sm">{t('category') || 'Category'}:</strong>{' '}
                                    {category.groupName || '-'}
                                </div>
                                <div>
                                    <strong className="font-medium text-sm">{t('type') || 'Stock Type'}:</strong>{' '}
                                    {category.groupType || '-'}
                                </div>
                            </div>

                            {/* Opening balance bar – mirrors LedgerDetails opening row */}
                            <div className="flex justify-between gap-3 mt-3">
                                <div className="flex gap-2">
                                    <div className="inline-block px-2 border-t border-e py-[2px] fw-medium">
                                        {t('openingStock')}: {category.opening_stock ?? 0}
                                    </div>
                                </div>
                                <div className="inline-block px-2 border-t border-s py-[2px] fw-medium">
                                    {t('closingStock')}: {closingStock}
                                </div>
                            </div>
                        </div>

                        {/* ── Table area (same structure as LedgerDetails) ──────────── */}
                        <div
                            ref={containerRef}
                            id="table-container"
                            tabIndex={0}
                            onKeyDown={handleKeyDown}
                            className="h-[64vh] overflow-auto p-0 m-0 relative outline-none"
                        >
                            {loading && (
                                <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60">
                                    <LoadingSpinner size={60} color="#000000" />
                                </div>
                            )}

                            <table className="w-full text-sm table-fixed">
                                <thead className="sticky top-0 py-1 mt-0 bg-theme-headerBg text-theme-color">
                                    <tr>
                                        <th className="py-[2px] px-1 font-medium text-center w-[50px]">
                                            {t('srNumber')}
                                        </th>
                                        <th className="py-[2px] px-1 font-medium text-start w-[100px]">
                                            {t('date')}
                                        </th>
                                        <th className="py-[2px] px-1 font-medium w-1/3">
                                            {t('description')}
                                        </th>
                                        <th className="py-[2px] px-1 font-medium text-end">
                                            {t('quantityIn')}
                                        </th>
                                        <th className="py-[2px] px-1 font-medium text-end">
                                            {t('quantityOut')}
                                        </th>
                                        <th className="py-[2px] px-1 font-medium text-center">
                                            {t('Action') || 'Action'}
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {hasItems ? (
                                        <>
                                            {items.map((item, index) => {
                                                const isFocused = index === focusedIndex;
                                                const isSelected = selectedRows.has(item.id);

                                                return (
                                                    <tr
                                                        key={item.id}
                                                        id={`row-${index}`}
                                                        ref={(el) => (rowRefs.current[index] = el)}
                                                        className={`cursor-pointer ${
                                                            isFocused
                                                                ? 'bg-[#5c5b5b] text-white'
                                                                : isSelected
                                                                ? 'bg-[#f4c48a]'
                                                                : ''
                                                        }`}
                                                    >
                                                        <td className="px-1 border-b text-center">{index + 1}</td>
                                                        <td className="px-1 border-b">
                                                            {item.created_at
                                                                ? new Date(item.created_at).toLocaleDateString('en-GB')
                                                                : '-'}
                                                        </td>
                                                        <td className="px-1 py-[2px] border-b">
                                                            {item.description}
                                                        </td>
                                                        <td className="px-1 border-b text-end">
                                                            {item.in_side ? item.in_side : ''}
                                                        </td>
                                                        <td className="px-1 py-[2px] border-b text-end">
                                                            {item.out_side ? item.out_side : ''}
                                                        </td>
                                                        <td className="px-1 text-center border-b">
                                                            <div className="flex justify-center gap-2">
                                                                <FaEdit
                                                                    className={`mt-[2px] cursor-pointer transition-colors ${
                                                                        item.stock_key
                                                                            ? 'text-gray-300 cursor-not-allowed'
                                                                            : isFocused
                                                                            ? 'text-white'
                                                                            : 'text-gray-700 hover:text-theme-btnBg'
                                                                    }`}
                                                                    onClick={() =>
                                                                        !item.stock_key && handleUpdateItem(item)
                                                                    }
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}

                                            {/* ── Footer totals row – mirrors LedgerDetails sticky total ── */}
                                            <tr className="sticky bottom-0 border-t border-theme-sidebarBg font-semibold bg-white">
                                                <td colSpan="3" className="px-1 py-1 text-start ps-3">
                                                    {t('total')}:
                                                </td>
                                                <td className="px-1 py-1 text-end">{totalIn}</td>
                                                <td className="px-1 py-1 text-end">{totalOut}</td>
                                                <td className="px-1 py-1" />
                                            </tr>
                                        </>
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="px-1 py-4 text-center text-gray-500">
                                                {t('noDataAvailable')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ── Closing stock bar – mirrors LedgerDetails closing balance bar ── */}
                        {hasItems && (
                            <div className="flex justify-between bg-white border-theme-headerBg border-t">
                                <div className="inline-block px-1 pt-[1px] fw-semibold">
                                    {t('quantityIn')}: {totalIn} &nbsp;|&nbsp; {t('quantityOut')}: {totalOut}
                                </div>
                                <div className="inline-block px-1 pe-3 pt-[1px] pb-[2px] fw-semibold">
                                    {t('closingStock')}: {closingStock}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── AddInventory edit modal (same as original – logic unchanged) ── */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                    <AddInventory
                        editItem={editItem}
                        handleSave={handleAddInventory}
                        handleCancel={() => {
                            setIsModalOpen(false);
                            setEditItem(null);
                        }}
                        loading={loading}
                    />
                </div>
            )}
        </>
    );
}